import { logout } from "@/store/slices";
import { ChevronRight, Home } from "lucide-react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

/**
 * Breadcrumb Component
 * ------------------------------------------------------------
 * Provides navigation breadcrumbs based on the current route.
 * - Dynamically generates breadcrumb items from URL segments.
 * - Displays "Home" icon link as the root.
 * - Supports custom labels for known routes.
 * - Shows Logout button on the right side.
 *
 */
const Breadcrumb = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pathnames = location.pathname.split("/").filter((x) => x);

  /**
   * Convert a path segment to a readable label
   * - Uses predefined labels for known routes
   * - Falls back to capitalizing the first letter
   *
   * @param {string} path - Path segment (e.g., "dashboard")
   * @returns {string} - Formatted label (e.g., "Dashboard")
   */
  const getLabel = (path) => {
    const labels = {
      dashboard: "Dashboard",
      settings: "Admin Settings",
    };

    return labels[path] || path.charAt(0).toUpperCase() + path.slice(1);
  };

  /**
   * Logs out the current user and redirects to the login page.
   *
   * Steps performed:
   * 1. Dispatches the `logout` action to clear user authentication state from Redux.
   * 2. Navigates the user to the "/login" route to enforce re-authentication.
   */
  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  return (
    <div className="flex items-center justify-end sm:justify-between px-6 py-4 bg-[linear-gradient(135deg,#002D3D_0%,#111A3D_48%,#332D62_100%)] border-b border-white/10">
      {/* ---------------- Left: Breadcrumb Navigation ---------------- */}
      <nav className="hidden sm:flex items-center space-x-2 text-sm text-slate-300">
        {/* Home link */}
        <Link
          to="/dashboard"
          className="flex items-center hover:text-neutral-100 transition-colors"
        >
          <Home className="h-4 w-4" />
        </Link>

        {/* Dynamic breadcrumb items based on path segments */}
        {pathnames.map((path, index) => {
          const routeTo = `/${pathnames.slice(0, index + 1).join("/")}`;
          const isFirstRoute = index === 0;

          return (
            <div key={`${path}-${index}`} className="flex items-center space-x-2">
              <ChevronRight className="h-4 w-4" />

              {isFirstRoute ? (
                // Only first route item is clickable
                <Link
                  to={routeTo}
                  className="hover:text-neutral-100 transition-colors"
                >
                  {getLabel(path)}
                </Link>
              ) : (
                // All other items are plain text
                <span className="text-neutral-100 font-medium">
                  {getLabel(path)}
                </span>
              )}
            </div>
          );
        })}
      </nav>

      {/* ---------------- Right: Actions (Logout) ---------------- */}
      <div className="flex items-center space-x-4">
        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="px-4 py-1.5 border border-cyan-400 text-neutral-100 rounded-full text-sm font-medium bg-brand-100/40 hover:bg-brand-100/60 transition"
        >
          Logout
        </button>
      </div>
    </div>
  );
};

export default Breadcrumb;